import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { CircularProgress, Box, Typography } from '@mui/material';

const OAuth2RedirectHandler = () => {
    const navigate = useNavigate();
    const { loginSuccess } = useAuth();

    useEffect(() => {
        const getUrlParameter = (name) => {
            name = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
            const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
            const results = regex.exec(window.location.search);
            const value = results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
            return value;
        };

        const token = getUrlParameter('token');
        const error = getUrlParameter('error');

        if (error) {
            console.error("Authentication error:", error);
            
            // Check if we're in a popup window
            if (window.opener) {
                try {
                    // Send the error to the parent window
                    window.opener.postMessage({
                        type: 'oauth2_error',
                        error: error,
                        message: 'Authentication failed'
                    }, window.location.origin);
                    
                    // Close the popup after sending the message
                    window.close();
                } catch (err) {
                    console.error('Error sending message to opener:', err);
                    // Fallback if postMessage fails
                    navigate('/', { 
                        state: { 
                            error: 'Authentication failed', 
                            errorDetails: error 
                        } 
                    });
                }
            } else {
                // Direct navigation with error
                navigate('/', { 
                    state: { 
                        error: 'Authentication failed', 
                        errorDetails: error 
                    } 
                });
            }
            return;
        }

        if (token) {
            
            // Check if we're in a popup window
            if (window.opener) {
                try {
                    // Send the token to the parent window
                    window.opener.postMessage({
                        type: 'oauth2_success',
                        token: token
                    }, window.location.origin);
                    
                    // Close the popup after sending the message
                    window.close();
                } catch (err) {
                    console.error('Error sending message to opener:', err);
                    // Fallback if postMessage fails
                    handleDirectLogin(token);
                }
            } else {
                // Direct login flow (not in popup)
                handleDirectLogin(token);
            }
        } else {
            console.error("No token found in URL");
            navigate('/', { 
                state: { 
                    error: 'No authentication token received' 
                } 
            });
        }
        // eslint-disable-next-line
    }, [navigate, loginSuccess]);

    const handleDirectLogin = async (token) => {
        
        // Authenticate with the token
        const success = await loginSuccess(token);
        
        if (success) {
            // Get the redirect path from localStorage if it exists
            const savedRedirect = localStorage.getItem('redirectAfterLogin');
            
            if (savedRedirect) {
                localStorage.removeItem('redirectAfterLogin');
                navigate(savedRedirect);
            } else {
                // If no saved redirect, go to home
                navigate('/');
            }
        } else {
            // Authentication failed
            navigate('/', { 
                state: { 
                    error: 'Authentication failed' 
                } 
            });
        }
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh'
        }}>
            <CircularProgress />
            <Typography sx={{ mt: 2 }}>
                Processing authentication...
            </Typography>
        </Box>
    );
};

export default OAuth2RedirectHandler;